import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core"
import { Field, Form, Formik } from "formik"
import { CheckboxWithLabel, TextField } from "formik-material-ui"
import { Autocomplete } from "formik-material-ui-lab"
import { useContext } from "react"
import { useMutation, useQueryClient } from "react-query"
import * as Yup from "yup"
import { updatePort } from "~/actions/labs"
import CounterHeader from "~/components/Common/CounterHeader"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const EditLabOutputDialog = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { dialogs, closeDialog, openSnackBar } = useContext(AppContext)

  const thisDialog = dialogs?.["editLabOutputDialog"] || {}
  const { open = false, data } = thisDialog
  const openErrorSnackbar = useOpenErrorSnackbar()
  const updatePortMutation = useMutation((data) =>
    updatePort(tab.lab_id, tab.id, data),
  )

  const handleClose = () => {
    closeDialog("editLabOutputDialog")
  }

  const onSubmit = (values, actions) => {
    updatePortMutation.mutate(values, {
      onSuccess: (res) => {
        queryClient.setQueryData(["lab", tab.id], (oldData) => {
          return res
        })
        openSnackBar({ message: "Tab Updated" })
        handleClose()
      },
      onError: (err) => {
        openErrorSnackbar(err, "Error Updating Tab")
      },
      onSettled: () => {
        actions.setSubmitting(false)
      },
    })
  }

  const tab = data?.data

  const schema = Yup.object().shape({
    name: Yup.string()
      .required("Please enter a name")
      .max(50, "Name must be less than 50 characters"),
    port: Yup.number().required("Please enter a port"),
    lab_instance_id: Yup.number().required("Must add an instance"),
  })

  return (
    <Dialog open={open}>
      <DialogTitle>
        <Typography variant="h2" className={classes.title}>
          Edit Output Tab
        </Typography>
        <Typography color="primary" variat="subtitle2">
          Update {tab?.name}
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={{
          name: tab?.name || "",
          port: tab?.port || "",
          check_before_ready: Boolean(tab?.check_before_ready),
          lab_instance_id: tab?.lab_instance_id,
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({ values, errors, touched, isSubmitting }) => (
          <Form>
            <DialogContent>
              <Box>
                <CounterHeader title="Name" value={values.name} max={50} />
                <Field
                  name="name"
                  component={TextField}
                  variant="filled"
                  fullWidth
                />
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Box marginTop="8px" width="50%">
                  <Typography color="secondary" className={classes.inputLabel}>
                    Port
                  </Typography>
                  <Field
                    name="port"
                    component={TextField}
                    variant="filled"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box marginTop="8px">
                <Typography color="secondary" className={classes.inputLabel}>
                  Select Instance
                </Typography>
                <Field
                  name="lab_instance_id"
                  options={data?.instances?.map((x) => {
                    return x.id
                  })}
                  component={Autocomplete}
                  disableClearable
                  placeholder="Select"
                  getOptionLabel={(x) => {
                    const instance = data?.instances?.find?.((y) => x === y.id)
                    return instance?.name
                  }}
                  renderInput={(params) => {
                    return (
                      <MuiTextField
                        {...params}
                        name="lab_instance_id"
                        variant="filled"
                        error={touched.instances && Boolean(errors.instance)}
                      />
                    )
                  }}
                  classes={{
                    inputRoot: classes.autocompleteRoot,
                  }}
                />
              </Box>
              <Box marginTop="16px" width="50%">
                <Field
                  type="checkbox"
                  component={CheckboxWithLabel}
                  value={values.check_before_ready}
                  checked={values.check_before_ready}
                  name="check_before_ready"
                  color="secondary"
                  Label={{ label: "Check Before Ready" }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <LoaderButton
                type="submit"
                variant="contained"
                color="secondary"
                size="small"
                classoverrides={{
                  wrapper: classes.button,
                  button: {
                    root: classes.buttonRoot,
                  },
                }}
                working={isSubmitting}
              >
                Save
              </LoaderButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditLabOutputDialog
